import { useLocation, useNavigate } from 'react-router-dom'
import type { TripSummary } from '@sp/shared'
import { api } from '../lib/api.ts'
import { HttpError } from '../lib/http.ts'
import { plural } from '../lib/format.ts'
import { forgetTrip } from '../lib/outbox.ts'
import { queueTripStatus } from '../lib/tripSync.ts'
import { errorMessage, invalidateResources } from '../lib/useResource.ts'
import { useConfirm } from './ConfirmSheet.tsx'
import type { ContextAction } from './ContextMenu.tsx'

/**
 * Akce nakupu v kontextovem menu (bocni panel, stranka Nakupy): stav jde pres
 * frontu jako v nakupu samotnem, mazani potrebuje signal a potvrzeni.
 */
export function useTripActions() {
  const navigate = useNavigate()
  const location = useLocation()
  const { confirm, confirmSheet } = useConfirm()

  async function remove(trip: TripSummary, title: string) {
    const ok = await confirm({
      title: `Smazat „${title}“?`,
      text: 'Nákup se smaže i se všemi položkami a nejde to vrátit.',
      confirmLabel: 'Smazat nákup',
      danger: true,
    })
    if (!ok) return
    try {
      await api.trips.remove(trip.id)
      forgetTrip(trip.id)
      invalidateResources(`sp:nakupy:${trip.groupId}`)
      if (location.pathname.startsWith(`/nakup/${trip.id}`)) navigate('/', { replace: true })
    } catch (err) {
      await confirm({
        title: 'Nákup se nesmazal',
        text: err instanceof HttpError && err.isOffline ? 'Mazat jde jen se signálem.' : errorMessage(err),
        confirmLabel: 'Rozumím',
      })
    }
  }

  async function finish(trip: TripSummary) {
    const left = trip.itemCount - trip.doneCount
    if (
      left > 0 &&
      !(await confirm({
        title: 'Dokončit nákup?',
        text: `Na seznamu ${left >= 2 && left <= 4 ? 'zbývají' : 'zbývá'} ${plural(left, 'položka', 'položky', 'položek')}. Nekoupené věci zůstanou v dokončeném nákupu.`,
        confirmLabel: 'Dokončit i tak',
      }))
    ) {
      return
    }
    queueTripStatus(trip, 'done')
  }

  function actionsFor(trip: TripSummary, title: string): ContextAction[] {
    const actions: ContextAction[] = [
      { label: 'Otevřít', icon: 'chevron_right', onSelect: () => navigate(`/nakup/${trip.id}`) },
    ]
    if (trip.status === 'planning') {
      actions.push({
        label: 'Nakupuju',
        icon: 'basket',
        disabled: trip.itemCount === 0,
        onSelect: () => {
          queueTripStatus(trip, 'shopping')
          navigate(`/nakup/${trip.id}/obchod`)
        },
      })
    }
    if (trip.status === 'shopping') {
      actions.push({ label: 'Zpět k plánování', icon: 'pencil', onSelect: () => queueTripStatus(trip, 'planning') })
    }
    if (trip.status !== 'done') {
      actions.push({ label: 'Dokončit nákup', icon: 'check', onSelect: () => void finish(trip) })
    } else {
      actions.push({ label: 'Vrátit mezi rozpracované', icon: 'refresh', onSelect: () => queueTripStatus(trip, 'shopping') })
    }
    actions.push(
      { label: 'Vytisknout seznam', icon: 'printer', onSelect: () => navigate(`/nakup/${trip.id}/tisk`) },
      { label: 'Smazat nákup', icon: 'trash', tone: 'error', onSelect: () => void remove(trip, title) },
    )
    return actions
  }

  return { actionsFor, tripActionsSheet: confirmSheet }
}
